import {
    Box, FormControl, Grid, Card, CardContent, Typography, Chip, Avatar, Tooltip, IconButton
} from "@mui/material";
import MaterialTable from "material-table";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import CommonDatePicker from "../../../Base/views/CommonDatePicker";
import CustomToggle from "../../../Base/views/CustomToggle";
import { useState, useEffect, useMemo, useCallback } from "react";
import { GetDailyProductivityAction, GetReportRecruiterAction } from "../../../../../store/ATS/Report/Action";
import { useDispatch, useSelector } from "react-redux";
import Chart from "react-apexcharts";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import VideoCameraFrontOutlinedIcon from "@mui/icons-material/VideoCameraFrontOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

// ─── Brand ─────────────────────────────────────────────────────────────────────
const BRAND = "#8B44FF";
const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};

const formatDate = (dateStr) => {
    try {
        const [y, m, d] = dateStr.split("-");
        const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        return `${d}-${months[parseInt(m) - 1]}-${y}`;
    } catch { return dateStr; }
};

const isoFromDate = (d) => {
    if (!d) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};

// ─── KPI Card ──────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, value, label, sub, color }) => (
    <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: `${color}18`, color, width: 40, height: 40 }}>{icon}</Avatar>
                <Box>
                    <Typography fontWeight={800} fontSize="1.3rem" lineHeight={1}>{value}</Typography>
                    <Typography fontSize="0.75rem" color="text.secondary">{label}</Typography>
                    {sub && <Typography fontSize="0.68rem" color={color} fontWeight={600}>{sub}</Typography>}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const DayTrackerReportNew = () => {
    const dispatch = useDispatch();
    const [localIsDashboardView, setLocalIsDashboardView] = useState(true);
    const today = todayStr();

    const [selectedDate, setSelectedDate] = useState(today);
    const [recruiterFilter, setRecruiterFilter] = useState("");
    const [userData, setUserData] = useState({ load: true, values: [] });

    const { atsDailyProductivity } = useSelector(state => state.AtsReducer?.AtsReportReducer);
    const { atsRecruiterData } = useSelector(state => state.AtsReducer?.AtsReportReducer);

    // ── Fetch recruiter list ──────────────────────────────────────────────────
    useEffect(() => {
        dispatch(GetReportRecruiterAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (atsRecruiterData?.length > 0) setUserData({ load: false, values: atsRecruiterData });
    }, [atsRecruiterData]);

    // ── Fetch day's productivity data ─────────────────────────────────────────
    const fetchData = useCallback(() => {
        dispatch(GetDailyProductivityAction({
            from_date: selectedDate,
            to_date: selectedDate,
            primary_recruiter: recruiterFilter
                ? `{${recruiterFilter.ats_users_id}}`
                : null,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate, recruiterFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ── Aggregate per recruiter for the selected single day ───────────────────
    const rows = atsDailyProductivity || [];

    const recruiterMap = useMemo(() => {
        const map = {};
        rows?.forEach(r => {
            const name = r.recruiter_name || "Unknown";
            if (!map[name]) map[name] = { profiles_shared: 0, interviews: 0, jobs: new Set() };
            map[name].profiles_shared += parseInt(r.shortlisted) || 0;
            map[name].interviews += parseInt(r.interviews_scheduled) || 0;
            if (r.job_title) map[name].jobs.add(r.job_title);
        });
        return map;
    }, [rows]);

    const recruiterSummary = Object.entries(recruiterMap)
        ?.sort(([, a], [, b]) => b.profiles_shared - a.profiles_shared)
        ?.map(([name, stats], i) => ({
            no: i + 1,
            recruiter_name: name,
            profiles_shared: stats.profiles_shared,
            interviews: stats.interviews,
            active_jds: stats.jobs.size,
        }));

    // KPIs
    const totalRecruiters = recruiterSummary?.length || 0;
    const totalProfiles = recruiterSummary?.reduce((s, r) => s + r.profiles_shared, 0) || 0;
    const totalInterviews = recruiterSummary?.reduce((s, r) => s + r.interviews, 0) || 0;
    const avgProfiles = totalRecruiters > 0 ? (totalProfiles / totalRecruiters).toFixed(1) : 0;

    // Bar chart: profiles shared per recruiter
    const barChart = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: recruiterSummary?.map(r => r.profiles_shared > 0 ? BRAND : "#E15759"),
            plotOptions: { bar: { horizontal: false, borderRadius: 5, distributed: true, columnWidth: "60%" } },
            dataLabels: { enabled: true, style: { fontSize: "11px", fontWeight: 700 } },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            xaxis: {
                categories: recruiterSummary?.map(r => r.recruiter_name.split(" ")[0]),
                labels: { style: { fontSize: "11px", fontWeight: 600 }, rotate: -30 },
            },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            legend: { show: false },
            tooltip: { y: { formatter: v => `${v} profiles` } },
        },
        series: [{ name: "Profiles Shared", data: recruiterSummary?.map(r => r.profiles_shared) }],
    };

    const stackedBar = {
        options: {
            chart: { type: "bar", fontFamily: "Nunito Sans", toolbar: { show: false } },
            colors: [BRAND, "#4E79A7"],
            plotOptions: { bar: { columnWidth: "55%", borderRadius: 4 } },
            dataLabels: { enabled: false },
            grid: { borderColor: "#F1F5F9", strokeDashArray: 4 },
            xaxis: {
                categories: recruiterSummary?.map(r => r.recruiter_name.split(" ")[0]),
                labels: { style: { fontSize: "11px", fontWeight: 600 }, rotate: -30 },
            },
            yaxis: { labels: { style: { fontSize: "11px", colors: "#94A3B8" } } },
            legend: { position: "top", fontSize: "12px", markers: { radius: 3 } },
        },
        series: [
            { name: "Profiles Shared", data: recruiterSummary?.map(r => r.profiles_shared) },
            { name: "Interviews", data: recruiterSummary?.map(r => r.interviews) },
        ],
    };

    const topPerformers = [...(recruiterSummary || [])].slice(0, 5);

    // Table columns
    const columns = [
        {
            title: "No.", field: "no", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center", width: 60 },
            headerStyle: { padding: "10px", textAlign: "center", width: 60 },
        },
        {
            title: "Recruiter Name", field: "recruiter_name",
            cellStyle: { padding: "10px", fontWeight: 600 }, headerStyle: { padding: "10px" },
        },
        {
            title: "Profiles Shared", field: "profiles_shared", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => (
                <Typography fontWeight={700} fontSize="0.88rem" color={r.profiles_shared > 0 ? BRAND : "#94A3B8"}>
                    {r.profiles_shared}
                </Typography>
            ),
        },
        {
            title: "Interviews", field: "interviews", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => (
                <Typography fontWeight={700} fontSize="0.88rem" color={r.interviews > 0 ? "#4E79A7" : "#94A3B8"}>
                    {r.interviews}
                </Typography>
            ),
        },
        {
            title: "Active JDs", field: "active_jds", type: "numeric",
            cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" },
            render: r => (
                <Chip label={r.active_jds} size="small"
                    sx={{ bgcolor: r.active_jds > 0 ? "#ECFDF5" : "#F1F5F9", color: r.active_jds > 0 ? "#059669" : "#94A3B8", fontWeight: 700 }}
                />
            ),
        },
    ];

    return (
        <>
            <Box sx={{ mt: 1 }}>
                {/* ── Filters bar & View Toggle ───────────────────────────── */}
                <Grid container columnSpacing={1} rowSpacing={1} mb={2} alignItems="center">
                    <Grid item xs={12} sm={4} md={3}>
                        <SelectableSearch
                            isOptionEqualToValue={(o, v) => o?.ats_users_id === v?.ats_users_id}
                            label="Recruiter"
                            value={recruiterFilter}
                            loading={() => setUserData({ load: false, values: atsRecruiterData })}
                            fieldLabel="first_name"
                            required={false}
                            variant="outlined"
                            name="primary_recruiter_filter"
                            onChangeEvent={(e, v) => { if (e === "primary_recruiter_filter") setRecruiterFilter(v || ""); }}
                            data={userData}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <FormControl fullWidth>
                            <CommonDatePicker
                                size="small" name="selected_date" label="Date" pickerType="date"
                                onKeyDown={e => e.preventDefault()}
                                pickerFormat="dd/MM/yyyy"
                                DefaultDTvalue={selectedDate}
                                maxDate={new Date()}
                                handleChange={v => { if (v) setSelectedDate(isoFromDate(v)); }}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs="auto">
                        <Chip
                            label={formatDate(selectedDate)} size="small"
                            sx={{ bgcolor: "#F3EDFD", color: BRAND, fontWeight: 700, fontSize: "0.78rem", px: 1 }}
                        />
                    </Grid>
                    <Grid item xs="auto">
                        <Tooltip title="Refresh" arrow>
                            <IconButton size="small" onClick={fetchData} sx={{ color: BRAND }}>
                                <RefreshIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, alignItems: "center" }}>
                        {setLocalIsDashboardView && (
                            <CustomToggle
                                isChecked={!localIsDashboardView}
                                handleToggleChange={() => setLocalIsDashboardView(!localIsDashboardView)}
                                Yestext={"Table"}
                                NoText={"Dashboard"}
                                YesTooltip={"Switch to Table View"}
                                NoTooltip={"Switch to Dashboard View"}
                                style={{ height: "37px", labelPadding: "2px 8px" }}
                            />
                        )}
                    </Grid>
                </Grid>

                {localIsDashboardView ? (
                    <>
                        {/* ── KPI Cards ─────────────────────────────────────────── */}
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<PeopleOutlineIcon sx={{ fontSize: 20 }} />} value={totalRecruiters} label="Active Recruiters" color="#4E79A7" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<ShareOutlinedIcon sx={{ fontSize: 20 }} />} value={totalProfiles} label="Profiles Shared" sub={`Avg: ${avgProfiles} per recruiter`} color={BRAND} />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<VideoCameraFrontOutlinedIcon sx={{ fontSize: 20 }} />} value={totalInterviews} label="Interviews Scheduled" color="#F28E2B" />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <KpiCard icon={<WorkOutlineOutlinedIcon sx={{ fontSize: 20 }} />} value={recruiterSummary?.reduce((s, r) => s + r.active_jds, 0) || 0} label="Active JDs Covered" color="#10B981" />
                            </Grid>
                        </Grid>

                        {/* ── Charts ────────────────────────────────────────────── */}
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} md={5}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Profiles Shared</Typography>
                                        <Typography variant="caption" color="text.secondary">Per recruiter on {formatDate(selectedDate)}</Typography>
                                        {(recruiterSummary?.length || 0) > 0 ? (
                                            <Chart options={barChart.options} series={barChart.series} type="bar" height={260} />
                                        ) : (
                                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 230, color: "#94A3B8", gap: 1 }}>
                                                <WorkOutlineOutlinedIcon sx={{ fontSize: 36, opacity: 0.4 }} />
                                                <Typography fontSize="0.82rem">No activity recorded for this date</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>Profiles vs Interviews</Typography>
                                        <Typography variant="caption" color="text.secondary">Activity comparison per recruiter</Typography>
                                        {(recruiterSummary?.length || 0) > 0 ? (
                                            <Chart options={stackedBar.options} series={stackedBar.series} type="bar" height={260} />
                                        ) : (
                                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 230, color: "#94A3B8" }}>
                                                <Typography fontSize="0.82rem">No data</Typography>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <Card sx={{ height: "100%" }}>
                                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                            <EmojiEventsIcon sx={{ fontSize: 16, mr: 0.5, color: "#FECB03", verticalAlign: "text-bottom" }} />
                                            Top Performers
                                        </Typography>
                                        {topPerformers?.map((r, i) => (
                                            <Box key={r.recruiter_name} sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                                                <Avatar sx={{ width: 30, height: 30, bgcolor: i < 3 ? `${medalColors[i]}20` : "#F1F5F9", color: i < 3 ? medalColors[i] : "#94A3B8", fontSize: "0.72rem", fontWeight: 800 }}>
                                                    #{i + 1}
                                                </Avatar>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography variant="body2" fontWeight={600} fontSize="0.8rem" noWrap>{r.recruiter_name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {r.profiles_shared} profiles · {r.interviews} interviews
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                        {topPerformers?.length === 0 && (
                                            <Typography fontSize="0.8rem" color="text.secondary">No data</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </>
                ) : (
                    /* ── Summary Table ─────────────────────────────────────── */
                    <Box sx={{ mt: 1 }}>
                        <Card>
                            <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                                <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <CalendarTodayIcon sx={{ fontSize: 15, color: BRAND }} />
                                            Day Tracker — {formatDate(selectedDate)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{totalRecruiters} recruiters tracked</Typography>
                                    </Box>
                                    {avgProfiles > 0 && (
                                        <Chip label={`Avg Profiles: ${avgProfiles}`} size="small"
                                            sx={{ bgcolor: "#FFF7E6", color: "#D97706", fontWeight: 700, fontSize: "0.75rem", border: "1px solid #FDE68A" }}
                                        />
                                    )}
                                </Box>

                                <MaterialTable
                                    columns={columns}
                                    data={recruiterSummary || []}
                                    options={{
                                        maxBodyHeight: "calc(100vh - 280px)",
                                        minBodyHeight: "350px",
                                        sorting: true,
                                        search: true,
                                        searchFieldAlignment: "right",
                                        searchAutoFocus: false,
                                        searchFieldVariant: "standard",
                                        filtering: false,
                                        exportButton: true,
                                        exportAllData: true,
                                        paging: true,
                                        pageSizeOptions: [25, 50, 100],
                                        pageSize: 25,
                                        actionsColumnIndex: -1,
                                        draggable: false,
                                        columnsButton: true,
                                        headerStyle: {
                                            backgroundColor: "#fff",
                                            color: "#64748B",
                                            fontWeight: 700,
                                            fontSize: "0.78rem",
                                            whiteSpace: "nowrap",
                                            padding: "10px",
                                        },
                                    }}
                                    title={`Day Tracker — ${formatDate(selectedDate)}`}
                                    style={{ boxShadow: "none" }}
                                />

                                {(recruiterSummary?.length || 0) > 0 && (
                                    <Box sx={{ display: "flex", gap: 3, px: 2.5, py: 1.2, bgcolor: "#F8FAFC", borderTop: "1px solid rgba(0,0,0,0.06)", flexWrap: "wrap" }}>
                                        <Typography variant="body2" fontWeight={800} color={BRAND}>
                                            {totalRecruiters} Recruiters
                                        </Typography>
                                        <Typography variant="body2" fontWeight={800} color={BRAND}>
                                            {totalProfiles} Total Profiles
                                        </Typography>
                                        <Typography variant="body2" fontWeight={800} color="#4E79A7">
                                            {totalInterviews} Total Interviews
                                        </Typography>
                                        <Typography variant="body2" fontWeight={700} color="#D97706">
                                            Avg: {avgProfiles} profiles/recruiter
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Box>
        </>
    );
};

export default DayTrackerReportNew;
